<script setup lang="ts">
  import { ref, onMounted, computed, nextTick, watch } from 'vue'
  import { CasesService, DocumentTemplatesService } from '@/types/generated/services.gen'
  import {
    type CustomerCase,
    type CustomerDataMapper as DataMapper,
    type CustomerDocumentTemplate,
    ImagePresetEnum,
    type ImageproxyImagePreset,
    type GenerateDocumentChatRequest,
    GenerateDocumentChatRoleEnum,
    type CreateWordDocumentRequestRequest
  } from '@/types/generated/types.gen'
  import router from '@/router'
  import '@tato30/vue-pdf/style.css'
  import TEMPORARYCaseBaseComponent from '@/components/app/cases/TEMPORARYCaseBaseComponent.vue'
  import BackIcon from '@/components/icons/backIcon.vue'
  import { ROUTES } from '@/router/routes'
  import RichTextEditor from '@/shared/components/RichTextEditor.vue'
  import SearchIcon from '@/components/icons/SearchIcon.vue'
  import { SendIcon } from 'lucide-vue-next'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import Image from '@/components/util/Image.vue'
  import TEMPORARYCaseDocumentGeneratorTemplateItem from '@/components/app/cases/TEMPORARYCaseDocumentGeneratorTemplateItem.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { ThumbsUpIcon, ThumbsDownIcon } from 'lucide-vue-next'
  import FeedbackModal from '@/components/app/cases/feedbackModal.vue'

  const caseData = ref<CustomerCase | null>(null)
  const mappers = ref<DataMapper[]>([])
  const templates = ref<CustomerDocumentTemplate[]>([])

  const editorContainer = ref(null)

  const fetchCase = async () => {
    caseData.value = (await CasesService.casesRetrieve({
      uuid: router.currentRoute.value.params.uuid.toString()
    })) as CustomerCase
  }

  const fetchMappers = async () => {
    mappers.value = (
      await CasesService.casesDatamappersList({
        uuid: router.currentRoute.value.params.uuid.toString()
      })
    ).results as DataMapper[]
  }

  const fetchTemplates = async () => {
    templates.value = (await DocumentTemplatesService.documentTemplatesList()).results
    if (templates.value.length > 0) {
      selectedTemplate.value = templates.value[0]
    }
  }

  onMounted(async () => {
    await Promise.all([fetchCase(), fetchMappers(), fetchTemplates()])
  })

  const selectedTemplate = ref<CustomerDocumentTemplate>()
  const selectedTemplateImage = computed<ImageproxyImagePreset>(() => {
    return selectedTemplate.value?.document.images?.find(
      (image) => image.image_preset === ImagePresetEnum.LARGE
    )
  })
  const step = ref<number>(1)
  const userInput = ref<string>('')
  const search = ref<string>('')
  const chat = ref<Array<GenerateDocumentChatRequest>>([])

  const messageTimestamps = ref(new Map())
  const chatContainer = ref(null)
  const showFeedbackModal = ref(false)

  const scrollToBottom = async () => {
    await nextTick()
    if (chatContainer.value) {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight
    }
  }

  /**
   * Stream the generated document content chunk by chunk.
   *
   * @param uuid The UUID of the case to retrieve the document for.
   * @param requestBody The request payload to send to the API.
   * @param onChunk A callback function invoked for each streamed chunk of data.
   * @returns A promise that resolves when the stream has fully consumed.
   * @throws Error if the request or streaming process fails.
   */
  async function streamGeneratedDocument(
    uuid: string,
    requestBody: Record<string, any>, // Update this type based on your payload structure
    onChunk: (chunk: string) => void // Callback to handle each stream chunk
  ): Promise<void> {
    const url = `/api/v3/cases/${uuid}/generate_document_html_stream/`

    function getCsrfToken(): string | undefined {
      const value = `; ${document.cookie}`
      const parts = value.split(`; csrftoken=`)
      if (parts.length === 2) return parts.pop()?.split(';').shift()
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': getCsrfToken()
      },
      body: JSON.stringify(requestBody)
    })

    // Ensure the response is okay
    if (!response.ok) {
      throw new Error(
        `Failed to fetch document stream. Status: ${response.status} ${response.statusText}`
      )
    }

    // Ensure the response is a stream
    if (!response.body) {
      throw new Error('Streaming is not supported or the response body is empty.')
    }

    const reader = response.body.getReader() // ReadableStream reader
    const decoder = new TextDecoder('utf-8') // Decode the binary chunks to strings
    const run = true

    try {
      while (run) {
        const { done, value } = await reader.read()
        if (done) break // Exit when all chunks are consumed

        const chunk = decoder.decode(value, { stream: true })
        onChunk(chunk) // Pass each chunk to the callback

        await nextTick()
        const editorInstance = editorContainer.value?.$el.querySelector('.fr-element')
        if (editorInstance) {
          // Scroll to the bottom of the editor
          editorInstance.scrollTop = editorInstance.scrollHeight
        }
      }
    } finally {
      reader.releaseLock() // Release the lock on the stream
    }
  }

  const isGeneratingDocument = ref<boolean>(false)
  const templateHtml = ref<string>('')

  const generateDocument = async () => {
    console.log('Generating document')
    isGeneratingDocument.value = true
    const requestBody = {
      document_template_uuid: selectedTemplate.value!.uuid,
      chat: chat.value
    }
    templateHtml.value = ''
    streamGeneratedDocument(
      router.currentRoute.value.params.uuid.toString(),
      requestBody,
      (chunk) => {
        templateHtml.value += chunk
      }
    )
      .then(() => {
        isGeneratingDocument.value = false
        const messageId = Date.now().toString()
        chat.value.push({
          role: GenerateDocumentChatRoleEnum.ASSISTANT,
          content: templateHtml.value,
          is_template: true
        })
        messageTimestamps.value.set(messageId, new Date())
      })
      .catch((error) => {
        console.error(error)
        isGeneratingDocument.value = false
      })
  }

  const sendUserInput = async () => {
    const messageId = Date.now().toString()
    chat.value.push({
      role: GenerateDocumentChatRoleEnum.USER,
      content: userInput.value,
      is_template: false
    })
    messageTimestamps.value.set(messageId, new Date())
    userInput.value = ''
    await scrollToBottom()
    await generateDocument()
  }
  const navigateToStepOne = async () => {
    step.value = 1
    chat.value = []
    templateHtml.value = ''
    isGeneratingDocument.value = false
  }

  const navigateToStepTwo = async () => {
    step.value = 2
    createDocumentRequest.value.name = selectedTemplate.value!.name
    chat.value = []
    await generateDocument()
  }

  const handleFeedbackClose = () => {
    showFeedbackModal.value = false
    navigateToStepOne()
  }

  const handleFeedbackSubmitted = (feedbackData: any) => {
    showFeedbackModal.value = false
    // Navigate back to case detail
    router.push(
      ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(':uuid', caseData.value.uuid)
    )
  }

  const saveDocument = async () => {
    createDocumentRequest.value.html = templateHtml.value
    isSavingDocument.value = true
    const response = await CasesService.casesCreateWordDocumentCreate({
      uuid: router.currentRoute.value.params.uuid.toString(),
      requestBody: createDocumentRequest.value
    })
    isSavingDocument.value = false
    savedDocumentUrl.value = response.url

    // Trigger a programmatic download
    const link = document.createElement('a') // Create a link element
    link.href = savedDocumentUrl.value // Set the href to the file URL
    link.download = `${createDocumentRequest.value.name}.docx` // Set the desired file name (optional)
    document.body.appendChild(link) // Append the element to the DOM
    link.click() // Trigger the click event (this starts the download)
    document.body.removeChild(link) // Remove it from the DOM afterward
    showFeedbackModal.value = true
  }

  const createDocumentRequest = ref<CreateWordDocumentRequestRequest>({
    name: '',
    html: '',
    create_pdf: true
  })

  const isSavingDocument = ref<boolean>(false)
  const savedDocumentUrl = ref<string>('')

  const filteredTemplates = computed(() => {
    if (search.value === '') {
      return templates.value
    }
    return templates.value.filter((template) =>
      template.name.toLowerCase().includes(search.value.toLowerCase())
    )
  })

  const onTemplateSelect = (template: CustomerDocumentTemplate) => {
    selectedTemplate.value = template
  }
  watch(() => chat.value.length, scrollToBottom)
  watch(() => isGeneratingDocument.value, scrollToBottom)
  watch(
    () => templateHtml.value,
    () => {
      nextTick(() => {
        if (isGeneratingDocument.value) {
          const container = editorContainer.value?.$el || editorContainer.value
          const editor = container?.querySelector('.ProseMirror')
          if (editor) {
            editor.scrollTop = editor.scrollHeight
          }
        }
      })
    }
  )

  onMounted(() => {
    scrollToBottom()
  })

  const positiveFeedback = ref<boolean | null>(null)
  const feedbackText = ref<string>('')
  const submitFeedback = async () => {
    console.log(`Feedback submitted: ${positiveFeedback.value}, ${feedbackText.value}`)
  }
</script>

<template>
  <TEMPORARYCaseBaseComponent
    :mappers="mappers"
    :case-data="caseData"
  >
    <div>
      <div class="mt-4">
        <router-link
          v-if="step == 1 || step == 3"
          :to="`${ROUTES.APP.CASES.CASE_DETAIL.LAW_SPECIFIC_DATA.replace(':uuid', caseData.uuid)}`"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
        >
          <BackIcon class="h-5 w-5" />
          Zurück
        </router-link>
        <button
          v-if="step == 2"
          @click="navigateToStepOne"
          class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
            font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:pointer-events-none disabled:opacity-50"
        >
          <BackIcon class="h-5 w-5" />
          Zurück
        </button>
      </div>
      <div
        v-if="step == 1 || step === 2"
        class="mt-4 rounded-xl border border-gray-200 bg-white p-4"
      >
        <div class="flex items-center gap-5">
          <!-- Left Section -->
          <div class="w-3/6">
            <div class="flex items-center gap-x-4">
              <!-- Step 1 -->
              <div
                class="text"
                :class="step == 1 ? 'font-bold' : 'text-gray-600'"
              >
                <span
                  class="mr-2 inline-flex items-center gap-x-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                  :class="step == 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'"
                >
                  1
                </span>
                Dokument auswählen
              </div>
              <!-- Step 2 -->
              <div
                class="text"
                :class="step == 2 ? 'font-bold' : 'text-gray-600'"
              >
                <span
                  class="mr-2 inline-flex items-center gap-x-1.5 rounded-lg px-3 py-1.5 text-xs font-medium"
                  :class="step == 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'"
                >
                  2
                </span>
                Dokument generieren
              </div>
            </div>
          </div>

          <!-- Right Section -->
          <div class="w-3/6">
            <div class="flex items-center justify-end gap-x-4">
              <!-- Button for Step 1 -->
              <button
                v-if="step == 1"
                @click="navigateToStepTwo"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm
                  font-medium text-white transition-colors hover:bg-purple-700 focus-visible:outline-none
                  focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                Dokument generieren
              </button>
              <CheckboxField
                v-if="step == 2"
                name="create_pdf"
                v-model="createDocumentRequest.create_pdf"
                label="PDF in Anfrage speichern"
                :disabled="isGeneratingDocument || isSavingDocument"
              />
              <button
                v-if="step == 2"
                @click="saveDocument"
                :disabled="isGeneratingDocument || isSavingDocument"
                class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm
                  font-medium text-white transition-colors hover:bg-purple-700 focus-visible:outline-none
                  focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                Word Datei herunterladen
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="step == 1"
          class="flex gap-5 pt-4"
        >
          <div class="w-5/12 border-r-2 pr-4">
            <div>
              <div class="mb-4">
                <!-- Search Input -->
                <div class="relative">
                  <div
                    class="pointer-events-none absolute inset-y-0 start-0 z-20 flex items-center ps-3.5"
                  >
                    <SearchIcon class="size-4 flex-shrink-0 text-gray-500" />
                  </div>
                  <input
                    v-model="search"
                    class="block w-full rounded-lg border-transparent bg-gray-100 px-3 py-[7px] ps-10 text-sm
                      focus:border-purple-500 focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
                    type="text"
                    :placeholder="'Dokument suchen'"
                  />
                </div>
                <!-- End Search Input -->
              </div>
              <TEMPORARYCaseDocumentGeneratorTemplateItem
                v-for="template in filteredTemplates"
                :key="template.uuid"
                :template="template"
                :isSelected="selectedTemplate && selectedTemplate.uuid === template.uuid"
                @select="onTemplateSelect"
              />
            </div>
          </div>
          <div class="w-7/12">
            <div class="rounded-xl border border-gray-200">
              <Image
                class="p-1"
                :src="selectedTemplateImage.url"
                v-if="selectedTemplateImage"
                :height="'800px'"
              />
            </div>
          </div>
        </div>
        <!-- The chat section -->
        <div
          v-if="step == 2"
          class="flex gap-5 pt-4"
        >
          <div class="w-4/12 border-r-2 pr-4">
            <div
              class="mx-auto flex h-[calc(100vh-300px)] max-w-md flex-col overflow-hidden rounded-lg border
                border-gray-100 bg-white shadow-sm"
            >
              <!-- Chat Messages Section -->
              <div
                ref="chatContainer"
                class="flex-1 overflow-y-auto bg-gray-50 px-6 py-4"
              >
                <!-- Welcome Message -->
                <div class="mb-6">
                  <div class="flex items-start space-x-3">
                    <div class="flex-1">
                      <div
                        class="rounded-2xl rounded-tl-none bg-white p-4 shadow-sm ring-1 ring-gray-100"
                      >
                        <p class="text-sm text-gray-800">
                          Möchten Sie Änderungen am Dokument vornehmen?
                        </p>
                      </div>
                      <div class="mt-1.5 flex items-center gap-x-2">
                        <span class="text-xs font-medium text-gray-500">JUPUS KI</span>
                        <span class="text-xs text-gray-400">
                          {{
                            new Date().toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Chat Messages -->
                <div
                  v-for="(message, index) in chat"
                  :key="index"
                  class="mb-6 last:mb-4"
                >
                  <!-- User Messages -->
                  <div
                    v-if="!message.is_template"
                    class="flex justify-end"
                  >
                    <div class="max-w-[85%]">
                      <div
                        class="rounded-2xl rounded-tr-none bg-gradient-to-r from-purple-600 to-purple-700 p-4 text-white shadow-sm"
                      >
                        <p class="text-sm">{{ message.content }}</p>
                      </div>
                      <div class="mt-1.5 flex items-center justify-end gap-x-2">
                        <span class="text-xs font-medium text-gray-500">Sie</span>
                        <span class="text-xs text-gray-400">
                          {{
                            new Date().toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- AI Messages -->
                </div>

                <!-- Loading Indicator - Only show when actually generating -->
                <div
                  v-if="isGeneratingDocument && templateHtml === ''"
                  class="flex items-start space-x-3"
                >
                  <div
                    class="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-purple-700"
                  >
                    <svg
                      class="h-4 w-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <div class="flex-1">
                    <div
                      class="rounded-2xl rounded-tl-none bg-white p-4 shadow-sm ring-1 ring-gray-100"
                    >
                      <div class="flex items-center space-x-2">
                        <div
                          class="h-2 w-2 animate-pulse rounded-full bg-purple-600 opacity-75"
                        ></div>
                        <div
                          class="h-2 w-2 animate-pulse rounded-full bg-purple-600 opacity-75"
                          style="animation-delay: 0.2s"
                        ></div>
                        <div
                          class="h-2 w-2 animate-pulse rounded-full bg-purple-600 opacity-75"
                          style="animation-delay: 0.4s"
                        ></div>
                      </div>
                    </div>
                    <div class="mt-1.5 flex items-center gap-x-2">
                      <span class="text-xs font-medium text-gray-500">Assistent</span>
                      <span class="text-xs text-gray-400">
                        {{
                          new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Chat Input Section -->
              <div class="border-t border-gray-100 bg-white px-6 py-4">
                <div class="flex space-x-2">
                  <TextAreaField
                    v-model="userInput"
                    name="userInput"
                    rows="3"
                    placeholder="Nachricht eingeben..."
                    :disabled="isGeneratingDocument"
                    class="flex-1 resize-none rounded-lg border-gray-200 text-sm shadow-sm placeholder:text-gray-400
                      focus:border-purple-500 focus:ring-purple-500 disabled:opacity-50"
                    @keydown.enter.prevent="sendUserInput"
                  />
                  <button
                    @click="sendUserInput"
                    type="button"
                    :disabled="isGeneratingDocument || !userInput.trim()"
                    class="inline-flex items-center gap-x-2 self-end rounded-lg bg-gradient-to-r from-purple-600 to-purple-700
                      px-4 py-3 text-sm font-medium text-white shadow-sm transition-all hover:from-purple-700
                      hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                      disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <SendIcon
                      v-if="!isGeneratingDocument"
                      class="h-5 w-5"
                    />
                    <svg
                      v-else
                      class="h-5 w-5 animate-spin"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                        fill="none"
                      />
                      <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </button>
                </div>
                <p class="mt-2 text-xs text-gray-500">Drücken Sie Enter zum Senden</p>
              </div>
            </div>
          </div>

          <div class="w-8/12">
            <div class="">
              <RichTextEditor
                ref="editorContainer"
                :model-value="templateHtml"
                :disabled="isGeneratingDocument"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <FeedbackModal
      v-if="showFeedbackModal"
      @close="handleFeedbackClose"
      @submitted="handleFeedbackSubmitted"
      :id="caseData?.uuid || ''"
    />
  </TEMPORARYCaseBaseComponent>
</template>
